import { ChevronDown, ChevronUp, ScanText, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ImageGallery } from "@/components/gallery/ImageGallery";
import { Button } from "@/components/ui/button";
import { JsonViewer } from "@/components/viewer/JsonViewer";
import { ActionsPanel } from "@/features/app/sections/ActionsPanel";
import { UploadSection } from "@/features/app/sections/UploadSection";
import { cn } from "@/lib/utils";
import { useBundleStore } from "@/stores/bundleStore";
import { useSourceStore } from "@/stores/sourceStore";

export function AppPage() {
  const { bundle, selectedSlideIndex, setSelected, reset } = useBundleStore();
  const clearSource = useSourceStore((s) => s.clear);
  const navigate = useNavigate();
  const [tab, setTab] = useState<"json" | "gallery">("json");
  const [actionsOpen, setActionsOpen] = useState(false);

  const handleReset = () => {
    const ok = window.confirm(
      "現在のファイル データをすべて削除して、別のファイルをアップロードできる状態にします。よろしいですか？",
    );
    if (ok) {
      reset();
      clearSource();
    }
  };

  if (!bundle) {
    return <UploadSection />;
  }

  const slide = bundle.structure.find((s) => s.slide_index === selectedSlideIndex);

  return (
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-4 px-4 py-5 sm:px-6 sm:py-8 md:gap-6 lg:grid-cols-[220px_1fr_260px]">
      {/* Slide picker: mobile = native select, desktop = vertical list */}
      <aside className="flex flex-col gap-2">
        <div className="mb-1 flex items-center justify-between gap-2 sm:mb-2">
          <h2 className="font-display text-xs font-medium uppercase tracking-wide text-[var(--color-ink-muted)] sm:text-sm">
            スライド ({bundle.manifest.slides.length})
          </h2>
          <button
            type="button"
            onClick={handleReset}
            title="別のファイルをアップロードし直す"
            aria-label="現在のファイル データを削除してアップロードし直す"
            className="inline-flex h-8 items-center gap-1 rounded-[8px] border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-[11px] text-[var(--color-ink-muted)] transition-colors hover:border-[var(--color-coral)] hover:bg-[var(--color-coral-soft)]/30 hover:text-[var(--color-coral-dark)]"
          >
            <Trash2 size={12} />
            <span>別のファイル</span>
          </button>
        </div>
        <p
          className="mb-1 truncate text-[11px] text-[var(--color-ink-soft)]"
          title={bundle.manifest.source.filename}
        >
          {bundle.manifest.source.filename}
        </p>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/layout", { state: { autoAnalyze: true } })}
          className="mb-2 w-full justify-start"
          title="同じファイルをレイアウト解析で開く"
        >
          <ScanText size={14} />
          レイアウト解析で開く
        </Button>

        <label className="lg:hidden" htmlFor="slide-select">
          <span className="sr-only">スライドを選択</span>
          <select
            id="slide-select"
            value={selectedSlideIndex ?? ""}
            onChange={(e) => setSelected(Number(e.target.value))}
            className="h-11 w-full rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm focus:border-[var(--color-coral)] focus:outline-none"
          >
            {bundle.manifest.slides.map((s) => (
              <option key={s.index} value={s.index}>
                {String(s.index).padStart(2, "0")} · {s.title}
              </option>
            ))}
          </select>
        </label>

        <div className="hidden max-h-[calc(100vh-8rem)] flex-col gap-1 overflow-auto pr-1 lg:flex">
          {bundle.manifest.slides.map((s) => (
            <button
              key={s.index}
              type="button"
              onClick={() => setSelected(s.index)}
              className={cn(
                "rounded-[10px] px-3 py-2 text-left text-sm transition-colors",
                s.index === selectedSlideIndex
                  ? "bg-[var(--color-coral)] text-white"
                  : "hover:bg-[var(--color-surface-muted)]",
              )}
            >
              <span
                className={cn(
                  "font-mono text-xs",
                  s.index === selectedSlideIndex ? "text-white/70" : "text-[var(--color-ink-soft)]",
                )}
              >
                {String(s.index).padStart(2, "0")}
              </span>{" "}
              {s.title}
            </button>
          ))}
        </div>
      </aside>

      {/* Main content */}
      <section className="min-w-0">
        <div
          role="tablist"
          className="mb-3 inline-flex w-full rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] p-1 text-sm sm:w-auto"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "json"}
            onClick={() => setTab("json")}
            className={cn(
              "flex-1 rounded-[8px] px-3 py-1.5 sm:flex-none",
              tab === "json" && "bg-[var(--color-coral)] text-white",
            )}
          >
            JSON ビューア
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "gallery"}
            onClick={() => setTab("gallery")}
            className={cn(
              "flex-1 rounded-[8px] px-3 py-1.5 sm:flex-none",
              tab === "gallery" && "bg-[var(--color-coral)] text-white",
            )}
          >
            画像 ({bundle.assets.media.length})
          </button>
        </div>

        {tab === "json" ? (
          <JsonViewer root={slide ?? {}} initialExpandedDepth={2} />
        ) : (
          <ImageGallery media={bundle.assets.media} />
        )}
      </section>

      {/* Actions: mobile = collapsible bottom section, desktop = fixed right column */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setActionsOpen((v) => !v)}
          aria-expanded={actionsOpen}
          aria-controls="actions-panel-mobile"
          className="flex w-full items-center justify-between rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm font-medium hover:bg-[var(--color-surface-muted)]"
        >
          <span>ダウンロード / コピー</span>
          {actionsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {actionsOpen && (
          <div id="actions-panel-mobile" className="mt-3">
            <ActionsPanel bundle={bundle} selectedSlide={slide} />
          </div>
        )}
      </div>
      <div className="hidden lg:block">
        <ActionsPanel bundle={bundle} selectedSlide={slide} />
      </div>
    </div>
  );
}
